import {
  Cable,
  CalendarCheck,
  CodeXml,
  Database,
  DatabaseZap,
  FileText,
  FileSignature,
  Files,
  FlaskConical,
  Gauge,
  HardHat,
  HeartPulse,
  LayoutGrid,
  Network,
  NotebookPen,
  PhoneOutgoing,
  Radio,
  ReceiptText,
  Server,
  Settings,
  Stethoscope,
  TestTubeDiagonal,
  UserCheck,
  Workflow,
  type LucideIcon,
} from 'lucide-react'
import { matchPath } from 'react-router-dom'
import { taskSearchOf } from '@/lib/shellSearch'
import type { FeatureFlags } from '@/types/configuration'

// Compile-time on/off switch for the /evals routes. Shared with mainRoutes.tsx
// so the manifest and the factory registry flip together — a manifest entry
// with no factory renders nothing.
export const EVALS_ENABLED: boolean = true

/** Params extracted by matchAppRoute. Values stay percent-encoded (raw). */
export type RouteParams = Record<string, string | undefined>

export interface AppRoute {
  /** Stable kind id, e.g. 'duty.detail'. */
  routeId: string
  /** react-router v7 pattern, e.g. '/duties/:dutyId/*'. */
  path: string
  /** Tab shelf icon. */
  icon?: LucideIcon
  /** Native-title tab tooltip. */
  tooltip?: string
  /**
   * Param values this entry must NOT match (the reserved-id blocklist as
   * data). matchAppRoute skips the entry on a hit. This blocklist is also why
   * AppRouteHost matches via matchAppRoute and never hands the manifest to
   * react-router's own ranking.
   */
  reservedParams?: Record<string, string[]>
  /** Capability gate (ProtectedCapabilityRoute). */
  capability?: string
  /** Org feature-flag gate (ProtectedFeatureRoute), composed OUTSIDE capability. */
  featureFlag?: keyof FeatureFlags
  /**
   * Identity gate: Clerk global admin, the permissions-matrix scope check, or
   * "the ACTIVE org is Careforce" (product-wide surfaces).
   */
  admin?: 'global' | 'permissions' | 'careforceOrg'
  /** Canonical tab route for this location; undefined = no shared tab. */
  tab?: (params: RouteParams) => string | undefined
  /**
   * Sub-views a splat entry fans out to (dispatch lives in mainRoutes.tsx's
   * factory). Data here so tests/test_capability_catalog.py can govern them.
   */
  subViews?: string[]
  /**
   * Keep this tab's subtree mounted while it sits in the background
   * (`<Activity mode="hidden">`). Every other tab unmounts on background and
   * remounts on select.
   *
   * The allowlist is deliberately tiny, and each entry pays for itself twice:
   * a named `reason` here, and a state-survival test that fails if the
   * retention stops working. Deleting the field is the kill switch — the tab
   * falls back to unmount-on-background with no other code change.
   */
  keepAlive?: { reason: string }
}

// One entry per route, ordered: literal paths stay above the dynamic siblings
// that would swallow them.
// NOTE: tests/test_capability_catalog.py parses this file TEXTUALLY — keep one
// object literal per entry with `path:` and `subViews:` as plain literals.
export const appRoutes: AppRoute[] = [
  {
    routeId: 'snippets.list',
    path: '/snippets',
    icon: CodeXml,
    tooltip:
      'Reusable blocks of markdown. Inserting one pastes a copy of its text — the script does not stay linked to it.',
    capability: 'snippets.view',
  },
  {
    routeId: 'duties.list',
    path: '/duties',
    icon: HardHat,
    capability: 'duties.view',
  },
  {
    routeId: 'duties.metrics',
    path: '/duties/metrics',
    icon: HardHat,
    // Both gates, deliberately: the flag is the org's rollout switch, the
    // capability then governs who inside such an org reaches it.
    featureFlag: 'duty_metrics',
    capability: 'duties.metrics.view',
  },
  {
    routeId: 'duty.detail',
    path: '/duties/:dutyId/*',
    icon: HardHat,
    tab: (p) => `/duties/${p.dutyId}`,
    subViews: ['call', 'playground', 'clients', 'robocall', 'texting'],
    keepAlive: {
      reason:
        'A duty tab holds many concurrently-edited cards and canvas state; ' +
        'remount would discard in-progress edits across ~10 editors.',
    },
  },
  {
    routeId: 'call.detail',
    path: '/calls/:callId',
    icon: PhoneOutgoing,
    admin: 'global',
  },
  {
    routeId: 'calls.list',
    path: '/calls',
    icon: PhoneOutgoing,
    capability: 'calls.list',
  },
  { routeId: 'messages.list', path: '/messages', capability: 'messages.list' },
  {
    routeId: 'signing.documents',
    path: '/documents',
    icon: FileSignature,
    admin: 'global',
    capability: 'data.clients.view',
  },
  {
    routeId: 'data.patients',
    path: '/data/patients',
    icon: UserCheck,
    capability: 'data.clients.view',
  },
  {
    routeId: 'data.appointments',
    path: '/data/appointments',
    icon: CalendarCheck,
    capability: 'data.appointments.view',
  },
  {
    routeId: 'data.claims',
    path: '/data/claims',
    icon: ReceiptText,
    capability: 'data.claims.view',
  },
  {
    routeId: 'data.providers',
    path: '/data/providers',
    icon: Stethoscope,
    capability: 'data.providers.view',
  },
  {
    routeId: 'data.visits',
    path: '/data/visits',
    icon: Network,
    capability: 'data.visits.view',
  },
  {
    routeId: 'data.labResults',
    path: '/data/lab-results',
    icon: TestTubeDiagonal,
    capability: 'data.labResults.view',
  },
  {
    routeId: 'data.chronicConditions',
    path: '/data/chronic-conditions',
    icon: HeartPulse,
    capability: 'data.chronicConditions.view',
  },
  {
    routeId: 'data.files',
    path: '/data/files',
    icon: Files,
    capability: 'data.files.view',
  },
  {
    routeId: 'data.ingestion',
    path: '/data/ingestion',
    icon: DatabaseZap,
    capability: 'data.ingestion.view',
  },
  {
    routeId: 'data.facts',
    path: '/data/facts',
    icon: Database,
    tooltip: 'Facts available to scripts and prompts for your organization.',
    capability: 'data.facts.view',
  },
  { routeId: 'notifications', path: '/notifications' },
  { routeId: 'docs.list', path: '/docs', icon: FileText },
  { routeId: 'doc.detail', path: '/docs/*', icon: FileText },
  { routeId: 'notes.list', path: '/notes', icon: NotebookPen },
  { routeId: 'queryResults', path: '/query-results' },
  {
    routeId: 'transcript.detail',
    path: '/transcripts/*',
    capability: 'transcript.detail',
  },
  { routeId: 'note.detail', path: '/notes/*', icon: NotebookPen },
  { routeId: 'billing', path: '/billing', icon: Gauge },
  {
    routeId: 'config',
    path: '/config',
    icon: Settings,
    capability: 'config.orgSelect',
  },
  { routeId: 'tools', path: '/tools', admin: 'global' },
  {
    routeId: 'signing.form',
    path: '/tools/document-signing/:formKey/:configurationId?',
    icon: FileText,
    admin: 'global',
  },
  {
    routeId: 'permissions',
    path: '/permissions',
    tooltip:
      'Which roles may use each capability. Careforce edits apply to every org; a customer org edits only itself.',
    // Scope comes from the active org: Careforce writes `*`, any other org
    // writes only its own slug.
    admin: 'permissions',
  },
  {
    routeId: 'connections',
    path: '/connections',
    icon: Cable,
    capability: 'connections.status',
  },
  {
    routeId: 'machines',
    path: '/machines',
    icon: Server,
    capability: 'machines.grid',
  },
  {
    routeId: 'evaluationRubrics',
    path: '/evaluation-rubrics',
    icon: TestTubeDiagonal,
    featureFlag: 'evaluation_rubrics',
    capability: 'config.evaluationRubrics.view',
  },
  {
    routeId: 'phoneLines',
    path: '/phone-lines',
    icon: LayoutGrid,
    capability: 'phoneLines.board',
  },
  {
    routeId: 'sessions.list',
    path: '/sessions',
    icon: Radio,
    capability: 'sessions.list',
  },
  {
    routeId: 'session.detail',
    path: '/sessions/:sessionId',
    icon: Radio,
    capability: 'sessionDetail.view',
  },
  {
    routeId: 'workflows.list',
    path: '/workflows',
    icon: Workflow,
    // Redirects to Connections, on Connections' own tab.
    tab: () => '/connections',
  },
  // ':slug/*' (not '/workflows/*') so '/workflows/' (empty slug) stays null.
  {
    routeId: 'workflow.detail',
    path: '/workflows/:slug/*',
    icon: Workflow,
    capability: 'workflows.detail',
    tab: (p) => `/workflows/${p.slug}`,
  },
  {
    routeId: 'memoryEvalCase.detail',
    path: '/evaluations/memory/cases/:caseId',
    icon: FlaskConical,
    admin: 'careforceOrg',
  },
  // Optional tail: '/evaluations' and '/evaluations/memory' are the same page,
  // and one entry (no named params) keeps them on ONE tab id.
  {
    routeId: 'memoryEvals',
    path: '/evaluations/memory?',
    icon: FlaskConical,
    admin: 'careforceOrg',
  },
  // The registry in mainRoutes.tsx mirrors this compile-time gate.
  ...(EVALS_ENABLED
    ? ([
        {
          routeId: 'evalCase.detail',
          path: '/evals/:dutyId/test-cases/:caseId',
          icon: FlaskConical,
          admin: 'global',
          tab: (p) => `/evals/${p.dutyId}/test-cases/${p.caseId}`,
          keepAlive: {
            reason:
              'An eval case holds prompt + config editors edited together.',
          },
        },
        {
          routeId: 'evalDuty.testCases',
          path: '/evals/:dutyId/test-cases',
          icon: FlaskConical,
          admin: 'global',
          tab: (p) => `/evals/${p.dutyId}/test-cases`,
        },
        {
          routeId: 'evalDuty.detail',
          path: '/evals/:dutyId',
          icon: FlaskConical,
          admin: 'global',
          reservedParams: {
            dutyId: [
              'personas',
              'test-cases',
              'run-history',
              'test-case-result',
              'run',
            ],
          },
        },
      ] satisfies AppRoute[])
    : []),
]

// Routes that deliberately carry no capability/featureFlag/admin gate, each
// with the reason. routeManifest.test.ts refuses a gateless entry that is not
// listed here.
export const UNGOVERNED_ROUTE_IDS: Record<string, string> = {
  'duty.detail':
    "Spans every duty sub-view; only the clients sub-view is gated (capability 'duty.clients.view', composed in its factory).",
  notifications:
    'The relay addresses every notification to one recipient and derives that ' +
    "recipient from the caller's own JWT, so the page can only ever show rows " +
    "that are already the viewer's.",
  'docs.list': 'Documents ship ungated; org scoping happens at the API.',
  'doc.detail': 'Same surface as /docs, opened on one document.',
  'notes.list': 'Meeting notes ship ungated.',
  'note.detail': 'Same surface as /notes, opened on one note.',
  queryResults:
    'Fetches conversation messages by the ids in its own URL; access control ' +
    "is the conversation-messages endpoint's own server-side auth. No " +
    'route-level capability exists for it.',
  billing: 'Billing ships ungated.',
  'workflows.list':
    'Redirect only: the list moved into /connections, which carries its own gate.',
}

// appRoutes is a module const, so the cache never invalidates. The cache is
// also load-bearing for render bail-out — AppRouteHost's element memo keys on
// the cached match object's identity.
const matchCache = new Map<
  string,
  { route: AppRoute; params: RouteParams } | null
>()

export function matchAppRoute(
  pathname: string
): { route: AppRoute; params: RouteParams } | null {
  const cached = matchCache.get(pathname)
  if (cached !== undefined) return cached
  const result = computeMatch(pathname)
  // Bounds a long session that visits many distinct detail paths.
  if (matchCache.size >= 500) matchCache.clear()
  matchCache.set(pathname, result)
  return result
}

function computeMatch(
  pathname: string
): { route: AppRoute; params: RouteParams } | null {
  for (const route of appRoutes) {
    const splat = route.path.endsWith('*')
    // Parity guards with the retired mainRoutes switch, which matchPath alone
    // breaks:
    // - exact arms reject a trailing slash ('/duties/' is not '/duties');
    // - literal-prefix splat arms require the slash ('/transcripts' bare does
    //   not enter '/transcripts/*').
    if (!splat && pathname.length > 1 && pathname.endsWith('/')) continue
    if (splat) {
      const base = route.path.slice(0, -2)
      if (!base.includes(':') && !pathname.startsWith(base + '/')) continue
    }
    const match = matchPath(
      { path: route.path, end: true, caseSensitive: true },
      pathname
    )
    if (!match) continue
    if (
      route.reservedParams &&
      Object.entries(route.reservedParams).some(([param, values]) =>
        values.includes(match.params[param] ?? '')
      )
    ) {
      continue
    }
    return { route, params: match.params }
  }
  return null
}

const routesById = new Map(appRoutes.map((r) => [r.routeId, r]))

/** The manifest entry for a routeId, or undefined when the id is unknown. */
export function routeById(routeId: string): AppRoute | undefined {
  return routesById.get(routeId)
}

// Named params of a pattern, in pattern order ('/evals/:dutyId/test-cases/:caseId'
// → ['dutyId', 'caseId']). The splat is not a named param.
const paramNamesCache = new Map<string, string[]>()
function paramNamesOf(pattern: string): string[] {
  let names = paramNamesCache.get(pattern)
  if (!names) {
    // Matches react-router's own param grammar: hyphenated and optional params
    // must not silently drop out of the tab identity.
    names = [...pattern.matchAll(/:([\w-]+)\??/g)].map((m) => m[1]!)
    paramNamesCache.set(pattern, names)
  }
  return names
}

/**
 * Stable tab identity for a matched location. Static routes use the routeId
 * alone; param routes append the param values in pattern order. Splat values
 * are excluded when the pattern has named params — all /duties/:dutyId
 * sub-views share `duty.detail:<dutyId>`. A pure-splat pattern (/docs/*)
 * keeps its splat value instead: there the splat IS the entity, and dropping
 * it would collapse every document into one tab.
 */
export function tabIdFor(
  routeId: string,
  params: Record<string, string | undefined>
): string {
  const entry = routesById.get(routeId)
  const values = entry
    ? paramNamesOf(entry.path).map((name) => params[name] ?? '')
    : []
  if (values.length === 0 && params['*'] !== undefined) {
    values.push(params['*'])
  }
  // Encoded before joining: a ':' inside a param value would otherwise collide
  // two different URLs onto one tab id.
  return values.length > 0
    ? `${routeId}:${values.map(encodeURIComponent).join(':')}`
    : routeId
}

interface ResolvedTabLocation {
  /** Stable tab identity (tabIdFor). */
  id: string
  /** Manifest entry kind, e.g. 'duty.detail'. */
  routeId: string
  /** Visited pathname + task-owned query + hash. */
  href: string
}

/**
 * Resolve a router location to its workspace-tab identity and href. The id is
 * canonical (all duty sub-views share one tab), but the href keeps the ACTUAL
 * visited pathname so selecting the tab restores the exact sub-view — plus
 * only the task-owned query keys; shell and ephemeral keys never land on a
 * tab.
 */
export function resolveTabLocation(location: {
  pathname: string
  search: string
  hash: string
}): ResolvedTabLocation | null {
  const visited = matchAppRoute(location.pathname)
  if (!visited) return null
  // An entry whose `tab` names another route's path (the /workflows redirect)
  // lives on that route's tab, never one of its own.
  const tabPath = visited.route.tab?.(visited.params)
  const matched = (tabPath && matchAppRoute(tabPath)) || visited
  return {
    id: tabIdFor(matched.route.routeId, matched.params),
    routeId: matched.route.routeId,
    href: location.pathname + taskSearchOf(location.search) + location.hash,
  }
}
